import { alsAnzeige } from "./formelrechner";
import { alsCsvZahl, liesZahl } from "./zahlformat";
import { zellschluessel } from "./zellbezug";

/*
  CSV aus- und einlesen.

  ---------------------------------------------------------------
  Deutsches Excel spricht Semikolon
  ---------------------------------------------------------------

  Das Komma ist in der deutschen Schreibweise das Dezimalzeichen. Als
  Trennzeichen zwischen Feldern ist es damit verbraucht — `1,5;2,5` geht,
  `1,5,2,5` nicht. Deutsches Excel benutzt deshalb das Semikolon, und wer
  eine Datei erzeugt, die dort ankommen soll, ebenfalls.

  ---------------------------------------------------------------
  Das BOM ist kein Zierrat
  ---------------------------------------------------------------

  Ohne die drei Bytes am Anfang liest Excel eine UTF-8-Datei als
  Windows-1252: Aus „Müller" wird „MÃ¼ller", aus „Straße" wird „StraÃŸe".
  Im Browser sieht die Datei völlig richtig aus — der Fehler zeigt sich
  erst auf dem Rechner des Empfängers. Genau deshalb steht das hier so
  ausführlich.
*/

const BOM = "﻿";
const TRENNER = ";";
const ZEILENENDE = "\r\n";

/*
  Ein Deckel gegen Dateien, die den Reiter anhalten.

  Eine CSV mit fünfzigtausend Zeilen ist schnell erzeugt und würde hier ein
  Yjs-Paket ergeben, das der Server ablehnt — nachdem der Browser eine
  Minute lang stand.
*/
export const HOECHSTENS_ZEILEN = 200;
export const HOECHSTENS_SPALTEN = 50;

/* ---------------------------------------------------------------
   Hinaus
   --------------------------------------------------------------- */

/*
  Das Blatt als CSV.

  Hinaus geht der GERECHNETE Wert, nicht die Formel: Eine CSV ist ein
  Ergebnis, kein Programm. `=SUMME(B2:B4)` hinge davon ab, dass die Zellen
  beim Empfänger genau dort landen, wo sie hier standen.

  Und die nackte Zahl — ohne Tausenderpunkt, ohne Euro, ohne Prozent.
  `1.234,50 €` ist für Excel Text, `1234,5` eine Zahl. Das Format ist eine
  Eigenschaft der Ansicht; wer damit weiterrechnen will, braucht die Zahl.
*/
export function nachCsv(gitter, werte) {
  const roh = gitter.zeilen.map((zeile) =>
    gitter.spalten.map((spalte) => {
      const wert = werte.get(zellschluessel(spalte.id, zeile.id));
      const anzeige = alsAnzeige(wert);
      if (anzeige.zahl !== null) return alsCsvZahl(anzeige.zahl);
      return anzeige.text || "";
    })
  );

  /*
    Leere Zeilen und Spalten am Ende fallen weg.

    Ein frisches Blatt hat zwanzig Zeilen und sechs Spalten; wer drei davon
    füllt, verschickt sonst eine Datei mit siebzehn Zeilen `;;;;;`. Beim
    Empfänger sieht das nach einem Fehler aus — und in Excel muss er sie von
    Hand löschen, bevor er sortieren kann.

    Weggeschnitten wird nur HINTEN. Eine leere Spalte A trägt Position und
    muss bleiben, sonst verrutscht alles dahinter.
  */
  let bisZeile = roh.length;
  while (bisZeile > 1 && roh[bisZeile - 1].every((f) => f === "")) bisZeile -= 1;

  const gefuellt = roh.slice(0, bisZeile);
  let bisSpalte = gitter.spalten.length;
  while (bisSpalte > 1 && spalteLeer(gefuellt, bisSpalte - 1)) bisSpalte -= 1;

  const zeilen = roh
    .slice(0, bisZeile)
    .map((reihe) => reihe.slice(0, bisSpalte).map(feld).join(TRENNER));

  return BOM + zeilen.join(ZEILENENDE) + ZEILENENDE;
}

function spalteLeer(reihen, stelle) {
  return reihen.every((reihe) => reihe[stelle] === "");
}

/*
  Ein Feld in Anführungszeichen setzen, wenn es sein muss.

  Nötig ist das bei Trennzeichen, Anführungszeichen und Umbrüchen im Text.
  Innere Anführungszeichen werden verdoppelt — so schreibt es die
  CSV-Gepflogenheit, und so liest es Excel.
*/
function feld(text) {
  if (!/[";\r\n]/.test(text)) return text;
  return `"${text.replace(/"/g, '""')}"`;
}

/* ---------------------------------------------------------------
   Herein
   --------------------------------------------------------------- */

/*
  Eine CSV in Reihen zerlegen.

  Der Trenner wird geraten: Wer eine Datei aus einem englischen Programm
  bekommt, hat Kommas darin. Gezählt wird in der ersten Zeile — außerhalb
  von Anführungszeichen, sonst zählte ein Komma in „Müller, Renate" mit.

  **Nichts wird als Formel gedeutet.** Ein Feld mit führendem `=` bleibt
  Text. Eine CSV ist Daten, und eine Datei aus fremder Hand soll im eigenen
  Blatt nicht anfangen zu rechnen.
*/
export function ausCsv(text) {
  const roh = typeof text === "string" ? text.replace(/^﻿/, "") : "";
  if (!roh.trim()) return { reihen: [], formate: [] };

  const trenner = rateTrenner(roh);
  const reihen = [];
  let reihe = [];
  let feldText = "";
  let inAnfuehrung = false;
  let i = 0;

  const feldFertig = () => {
    reihe.push(feldText);
    feldText = "";
  };
  const reiheFertig = () => {
    feldFertig();
    reihen.push(reihe);
    reihe = [];
  };

  while (i < roh.length) {
    const z = roh[i];

    if (inAnfuehrung) {
      if (z === '"') {
        if (roh[i + 1] === '"') {
          feldText += '"';
          i += 2;
          continue;
        }
        inAnfuehrung = false;
        i += 1;
        continue;
      }
      feldText += z;
      i += 1;
      continue;
    }

    if (z === '"' && feldText === "") {
      inAnfuehrung = true;
      i += 1;
      continue;
    }
    if (z === trenner) {
      feldFertig();
      i += 1;
      continue;
    }
    /* `\r\n`, `\n` und `\r` gelten alle drei — je nach Herkunft der Datei. */
    if (z === "\r" || z === "\n") {
      reiheFertig();
      if (z === "\r" && roh[i + 1] === "\n") i += 2;
      else i += 1;
      continue;
    }

    feldText += z;
    i += 1;
  }

  if (feldText !== "" || reihe.length > 0) reiheFertig();

  /* Eine leere letzte Zeile entsteht bei jedem Zeilenende am Dateiende. */
  while (reihen.length > 0 && reihen[reihen.length - 1].every((f) => f === "")) {
    reihen.pop();
  }

  const beschnitten = reihen
    .slice(0, HOECHSTENS_ZEILEN)
    .map((r) => r.slice(0, HOECHSTENS_SPALTEN));

  return {
    reihen: beschnitten,
    formate: rateFormate(beschnitten),
    zuVieleZeilen: reihen.length > HOECHSTENS_ZEILEN,
    zuVieleSpalten: reihen.some((r) => r.length > HOECHSTENS_SPALTEN),
  };
}

function rateTrenner(text) {
  const ersteZeile = text.split(/\r\n|\n|\r/)[0] || "";
  const zaehle = (zeichen) => {
    let anzahl = 0;
    let inAnfuehrung = false;
    for (let i = 0; i < ersteZeile.length; i += 1) {
      if (ersteZeile[i] === '"') inAnfuehrung = !inAnfuehrung;
      else if (!inAnfuehrung && ersteZeile[i] === zeichen) anzahl += 1;
    }
    return anzahl;
  };

  const kandidaten = [
    { zeichen: ";", anzahl: zaehle(";") },
    { zeichen: "\t", anzahl: zaehle("\t") },
    { zeichen: ",", anzahl: zaehle(",") },
  ];
  const beste = kandidaten.reduce((a, b) => (b.anzahl > a.anzahl ? b : a));
  /* Im Zweifel Semikolon — das ist die Herkunft, die hier zu erwarten ist. */
  return beste.anzahl > 0 ? beste.zeichen : ";";
}

/*
  Das Format einer Spalte raten.

  ---------------------------------------------------------------
  Die erste Zeile zählt nicht mit
  ---------------------------------------------------------------

  Fast jede Tabelle beginnt mit einer Überschriftenzeile: „Position",
  „Ansatz", „Anteil". Zählte man sie mit, hätte eine Spalte mit drei
  Beträgen und einer Überschrift nur drei Viertel Zahlen — und würde als
  Text eingelesen. Die Beträge stünden danach linksbündig da, ohne Euro,
  und man müsste jede Spalte von Hand nachstellen.

  Genau das ist beim ersten Prüflauf passiert. Deshalb: Ab zwei Zeilen
  bleibt die erste außen vor.

  Danach entscheidet die Mehrheit der GEFÜLLTEN Zellen — ein einzelner
  Ausreißer, etwa ein „siehe unten" in einer Zahlenspalte, soll sie nicht
  umwerfen.
*/
export function rateFormate(reihen) {
  const breite = Math.max(0, ...reihen.map((r) => r.length));
  const formate = [];
  const zumRaten = reihen.length > 1 ? reihen.slice(1) : reihen;

  for (let s = 0; s < breite; s += 1) {
    let gefuellt = 0;
    let zahlen = 0;
    let euro = 0;
    let prozent = 0;

    for (const reihe of zumRaten) {
      const inhalt = (reihe[s] || "").trim();
      if (!inhalt) continue;
      gefuellt += 1;
      if (liesZahl(inhalt) === null) continue;
      zahlen += 1;
      if (inhalt.endsWith("€")) euro += 1;
      if (inhalt.endsWith("%")) prozent += 1;
    }

    if (gefuellt === 0 || zahlen / gefuellt < 0.8) {
      formate.push("text");
    } else if (prozent > zahlen / 2) {
      formate.push("prozent");
    } else if (euro > zahlen / 2) {
      formate.push("euro");
    } else {
      formate.push("zahl");
    }
  }

  return formate;
}

/*
  Eine Datei zum Herunterladen anbieten.

  Über einen Verweis, den es nur für einen Augenblick gibt — es gibt keinen
  anderen Weg, im Browser eine Datei zu erzeugen, ohne sie erst irgendwohin
  hochzuladen. Und hochladen käme nicht in Frage: Der Inhalt gehört der
  Fraktion.
*/
export function biete(dateiname, inhalt) {
  const brocken = new Blob([inhalt], { type: "text/csv;charset=utf-8" });
  const adresse = URL.createObjectURL(brocken);
  const verweis = document.createElement("a");
  verweis.href = adresse;
  verweis.download = dateiname;
  document.body.appendChild(verweis);
  verweis.click();
  document.body.removeChild(verweis);
  /* Aufräumen — sonst hält der Browser die Daten bis zum Neuladen fest. */
  setTimeout(() => URL.revokeObjectURL(adresse), 1000);
}
